import { useEffect, useState, useCallback } from 'react';
import { useAuthStore, API_BASE, apiFetch } from '../store/auth';
import {
  ErrorMessage,
  Card,
  Badge,
  PageHeader,
  NoOrganizationMessage,
  Table,
  DataTable,
  Button,
  Select,
  DateRangePicker,
  Pagination,
  formatDate,
  formatEuro,
} from '../components';

interface MissingReceipt {
  id: string;
  date: string;
  amount: number;
  description: string | null;
  counterparty: string | null;
  source: string;
  receiptStatus: 'missing' | 'waived' | 'pending' | 'matched';
  receiptNotes: string | null;
  missingReceiptDetectedAt: string | null;
}

interface MissingReceiptSummary {
  totalMissing: number;
  bySource: Record<string, number>;
  byAge: {
    recent: number;
    medium: number;
    old: number;
  };
}

const STATUS_COLORS: Record<string, string> = {
  missing: 'bg-red-100 text-red-800',
  waived: 'bg-yellow-100 text-yellow-800',
  pending: 'bg-gray-100 text-gray-800',
  matched: 'bg-green-100 text-green-800',
};

const STATUS_LABELS: Record<string, string> = {
  missing: 'Puuttuu',
  waived: 'Vapautettu',
  pending: 'Odottaa',
  matched: 'OK',
};

const SOURCE_LABELS: Record<string, string> = {
  holvi: 'Holvi',
  op: 'OP',
  nordea: 'Nordea',
  sumup: 'SumUp',
  manual: 'Manuaalinen',
};

export default function MissingReceiptsPage() {
  const { activeOrg } = useAuthStore();
  const [transactions, setTransactions] = useState<MissingReceipt[]>([]);
  const [summary, setSummary] = useState<MissingReceiptSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [detecting, setDetecting] = useState(false);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [filters, setFilters] = useState({
    source: '',
    startDate: '',
    endDate: '',
  });
  const limit = 25;

  const fetchMissingReceipts = useCallback(async () => {
    if (!activeOrg) return;

    setLoading(true);
    setError('');

    try {
      const offset = (currentPage - 1) * limit;
      const params = new URLSearchParams();
      params.append('organization_id', activeOrg.id);
      params.append('limit', String(limit));
      params.append('offset', String(offset));
      if (filters.source) params.append('source', filters.source);
      if (filters.startDate) params.append('start_date', filters.startDate);
      if (filters.endDate) params.append('end_date', filters.endDate);

      const response = await apiFetch(`${API_BASE}/missing-receipts?${params.toString()}`);
      if (!response.ok) throw new Error('Failed to fetch');
      const data = await response.json();

      setTransactions(data.transactions);
      setTotalCount(data.pagination.total);
    } catch (err) {
      setError('Tositteiden haku epäonnistui');
      console.error('Failed to fetch missing receipts:', err);
    } finally {
      setLoading(false);
    }
  }, [activeOrg, currentPage, filters]);

  const fetchSummary = useCallback(async () => {
    if (!activeOrg) return;

    try {
      const response = await apiFetch(`${API_BASE}/missing-receipts/summary?organization_id=${activeOrg.id}`);
      if (!response.ok) throw new Error('Failed to fetch');
      const data = await response.json();
      setSummary(data);
    } catch (err) {
      console.error('Failed to fetch summary:', err);
    }
  }, [activeOrg]);

  const runDetection = async () => {
    if (!activeOrg) return;

    setDetecting(true);
    setError('');

    try {
      const response = await apiFetch(`${API_BASE}/missing-receipts/detect`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          organization_id: activeOrg.id,
          source: filters.source || undefined,
          start_date: filters.startDate || undefined,
          end_date: filters.endDate || undefined,
        }),
      });

      if (!response.ok) throw new Error('Failed to run detection');
      const data = await response.json();

      // Refresh the list
      await fetchMissingReceipts();
      await fetchSummary();

      alert(`Tunnistettu ${data.markedAsMissing} uutta puuttuvaa tositetta`);
    } catch (err) {
      setError('Tunnistus epäonnistui');
      console.error('Failed to run detection:', err);
    } finally {
      setDetecting(false);
    }
  };

  const markAsWaived = async (transactionId: string, notes?: string) => {
    if (!activeOrg) return;

    try {
      const response = await apiFetch(`${API_BASE}/missing-receipts/transactions/${transactionId}/mark-waived`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          organization_id: activeOrg.id,
          notes: notes || 'Vapautettu käyttäjän toimesta',
        }),
      });

      if (!response.ok) throw new Error('Failed to mark as waived');

      // Refresh the list
      await fetchMissingReceipts();
      await fetchSummary();
    } catch (err) {
      setError('Tilanteen päivitys epäonnistui');
      console.error('Failed to mark as waived:', err);
    }
  };

  useEffect(() => {
    fetchMissingReceipts();
    fetchSummary();
  }, [fetchMissingReceipts, fetchSummary]);

  if (!activeOrg) {
    return <NoOrganizationMessage />;
  }

  return (
    <div className="p-6 space-y-6">
      <PageHeader title="Puuttuvat tositteet" />

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card title="Puuttuvat tositteet" className="border-l-4 border-red-500">
            <div className="text-3xl font-bold text-red-600">{summary.totalMissing}</div>
            <div className="text-sm text-gray-500">vaatii tositteen</div>
          </Card>
          <Card title="Alle 30 pv" className="border-l-4 border-yellow-500">
            <div className="text-3xl font-bold text-yellow-600">{summary.byAge.recent}</div>
            <div className="text-sm text-gray-500">tuoretta tapahtumaa</div>
          </Card>
          <Card title="30-90 pv" className="border-l-4 border-orange-500">
            <div className="text-3xl font-bold text-orange-600">{summary.byAge.medium}</div>
            <div className="text-sm text-gray-500">keskeneräistä</div>
          </Card>
          <Card title="Yli 90 pv" className="border-l-4 border-red-700">
            <div className="text-3xl font-bold text-red-800">{summary.byAge.old}</div>
            <div className="text-sm text-gray-500">vanhaa tapahtumaa</div>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card title="Suodattimet ja toiminnot">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Select
            label="Lähde"
            value={filters.source}
            onChange={(e) => setFilters(f => ({ ...f, source: e.target.value }))}
            options={[
              { value: '', label: 'Kaikki lähteet' },
              { value: 'holvi', label: 'Holvi' },
              { value: 'op', label: 'OP' },
              { value: 'nordea', label: 'Nordea' },
              { value: 'sumup', label: 'SumUp' },
              { value: 'manual', label: 'Manuaalinen' },
            ]}
          />
          <DateRangePicker
            startLabel="Alkaen"
            endLabel="Päättyen"
            startDate={filters.startDate}
            endDate={filters.endDate}
            onStartDateChange={(v) => setFilters(f => ({ ...f, startDate: v }))}
            onEndDateChange={(v) => setFilters(f => ({ ...f, endDate: v }))}
          />
          <div className="flex items-end gap-2">
            <Button variant="primary" onClick={runDetection} disabled={detecting}>
              {detecting ? '🔍 Tunnistetaan...' : '🔍 Tunnista puuttuvat'}
            </Button>
            <Button variant="secondary" onClick={fetchMissingReceipts}>
              🔄 Päivitä
            </Button>
          </div>
        </div>
      </Card>

      {/* Error */}
      {error && <ErrorMessage message={error} />}

      {/* Missing Receipts Table */}
      <Card title={`Puuttuvat tositteet (${totalCount})`}>
        <DataTable>
          <Table
            columns={[
              {
                key: 'date',
                header: 'Päivämäärä',
                width: '120px',
                render: (tx: MissingReceipt) => formatDate(tx.date),
              },
              {
                key: 'source',
                header: 'Lähde',
                width: '100px',
                render: (tx: MissingReceipt) => SOURCE_LABELS[tx.source] || tx.source,
              },
              {
                key: 'counterparty',
                header: 'Vastapuoli',
                render: (tx: MissingReceipt) => tx.counterparty || tx.description || '—',
              },
              {
                key: 'amount',
                header: 'Summa',
                width: '120px',
                align: 'right',
                render: (tx: MissingReceipt) => (
                  <span className={tx.amount >= 0 ? 'text-green-600' : 'text-red-600'}>
                    {formatEuro(tx.amount)}
                  </span>
                ),
              },
              {
                key: 'status',
                header: 'Tila',
                width: '120px',
                render: (tx: MissingReceipt) => (
                  <Badge className={STATUS_COLORS[tx.receiptStatus]}>
                    {STATUS_LABELS[tx.receiptStatus]}
                  </Badge>
                ),
              },
              {
                key: 'actions',
                header: '',
                width: '150px',
                align: 'right',
                render: (tx: MissingReceipt) => (
                  <div className="flex gap-2 justify-end">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => window.location.href = `/transactions?id=${tx.id}`}
                    >
                      📄
                    </Button>
                    {tx.receiptStatus === 'missing' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          if (confirm('Merkitäänkö tosite vapautetuksi?')) {
                            markAsWaived(tx.id);
                          }
                        }}
                      >
                        ✓
                      </Button>
                    )}
                  </div>
                ),
              },
            ]}
            data={transactions}
            keyExtractor={(tx) => tx.id}
            loading={loading}
            emptyMessage="Ei puuttuvia tositteita valituilla suodattimilla"
          />
        </DataTable>

        {/* Pagination */}
        {totalCount > limit && (
          <div className="mt-4">
            <Pagination
              currentPage={currentPage}
              totalPages={Math.ceil(totalCount / limit)}
              onPageChange={setCurrentPage}
              totalItems={totalCount}
            />
          </div>
        )}
      </Card>
    </div>
  );
}
